import { useCallback, useEffect, useRef, useState } from 'react';
import { GooglePlacesAutocompleteProps, GooglePlacesPayload } from './types';
import { initGoogleMapsAPI } from './utils';

/**
 * Controller hook for Google Places Autocomplete input.
 *
 * Responsibilities:
 * - Lazily load the Google Maps JavaScript API (with Places library)
 * - Manage the controlled input value for the autocomplete field
 * - Bridge Google Autocomplete events to the app-level `onPlaceSelect` callback
 * - Normalize Google Place results into a predictable payload shape
 *
 * This hook intentionally isolates Google-specific behavior so the UI component
 * remains mostly declarative and easy to reason about.
 */
const useController = ({ onPlaceSelect }: Partial<GooglePlacesAutocompleteProps>) => {
  /**
   * Tracks whether the Google Maps API (specifically `google.maps.places`)
   * is available and ready to use.
   *
   * We initialize this lazily to support:
   * - SSR / test environments
   * - delayed script loading via `initGoogleMapsAPI`
   */
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(
    () => typeof window !== 'undefined' && !!window.google?.maps?.places
  );

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState('');

  /**
   * Ensures the Google Maps JS API is loaded before rendering the autocomplete.
   *
   * - `initGoogleMapsAPI` is idempotent and safe to call multiple times.
   * - Once resolved, we confirm that `google.maps.places` is available
   *   before marking the API as ready.
   */
  useEffect(() => {
    if (googleMapsLoaded) return;

    initGoogleMapsAPI().then(() => {
      setGoogleMapsLoaded(!!window.google?.maps?.places);
    });
  }, [googleMapsLoaded]);

  const onLoad = useCallback((ac: google.maps.places.Autocomplete) => {
    autocompleteRef.current = ac;
  }, []);

  /**
   * Handles selection of a place from the Google Autocomplete dropdown.
   *
   * Flow:
   * 1. Read the selected Place object from the Autocomplete instance
   * 2. Derive a user-facing label (formatted address preferred)
   * 3. Update the controlled input value so the UI reflects the selection
   * 4. Blur the input to remove the focus from the input after selection
   * 5. Normalize the Google Place into an app-friendly payload
   * 6. Forward the payload to `onPlaceSelect`
   *
   * Notes:
   * - We defensively guard against missing data because Google does not
   *   guarantee all fields will be present.
   * - The raw Google Place object (`gmaps`) is intentionally passed through
   *   for downstream consumers that need advanced address parsing.
   */
  const handlePlaceChanged = useCallback(() => {
    const ac = autocompleteRef.current;
    if (!ac?.getPlace) return;

    const place = ac.getPlace();
    if (!place) return;

    // Prefer a formatted address; fall back to name or current input value
    const selectedValue = place.formatted_address ?? place.name ?? value;

    setValue(selectedValue);

    inputRef.current?.blur?.();

    const loc = place.geometry?.location;
    const lat = loc?.lat?.();
    const lng = loc?.lng?.();

    const payload: GooglePlacesPayload = {
      description: selectedValue,
      gmaps: place,
      label: selectedValue,
      location: lat != null && lng != null ? { lat, lng } : undefined,
      placeId: place.place_id,
    };

    onPlaceSelect?.(payload);
  }, [onPlaceSelect, value]);

  return {
    googleMapsLoaded,
    handlePlaceChanged,
    inputRef,
    onLoad,
    setValue,
    value,
  };
};

export default useController;
